import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { UserInfo, TelegramAuthData, VkAuthData, AuthResponse } from '@/types';
import { authApi } from '@/api';
import { setTokens, clearTokens, getAccessToken } from '@/api/client';
import i18n from '@/i18n';
import { isAxiosError } from 'axios';

// Общий промис незавершённой проверки /auth/me (дедуп параллельных checkAuth)
let checkAuthInFlight: Promise<void> | null = null;

// Эпоха сессии: растёт при login/logout/applyAuthResponse/setUser. Поздний ответ
// /auth/me из прошлой эпохи не должен трогать стор и токены (S5-53 F3).
// Сравнение по accessToken не годится: успешный refresh в интерсепторе тоже меняет токен.
let authEpoch = 0;

/**
 * Auth Store - Zustand store для аутентификации
 * @contract Управление состоянием аутентификации пользователя
 */

interface AuthState {
  // Состояние
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Действия
  loginWithTelegram: (data: TelegramAuthData) => Promise<void>;
  loginWithVk: (data: VkAuthData) => Promise<void>;
  /**
   * Применить готовый AuthResponse (токены уже сохранены) — используется
   * когда auth-ответ получен из code-auth flow или другого внешнего источника.
   */
  applyAuthResponse: (response: AuthResponse) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: UserInfo) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Начальное состояние
      user: null,
      isAuthenticated: false,
      isLoading: false, // Изменено с true на false
      error: null,

      // Аутентификация через Telegram
      loginWithTelegram: async (data: TelegramAuthData) => {
        authEpoch += 1;
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.loginWithTelegram(data);
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : i18n.t('auth.errors.loginFailed');
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
            user: null,
          });
          throw error;
        }
      },

      // Аутентификация через VK ID
      loginWithVk: async (data: VkAuthData) => {
        authEpoch += 1;
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.loginWithVk(data);
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : i18n.t('auth.errors.vkLoginFailed');
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
            user: null,
          });
          throw error;
        }
      },

      // Применить готовый AuthResponse — токены УЖЕ сохранены вызывающим.
      applyAuthResponse: (response: AuthResponse) => {
        authEpoch += 1;
        set({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      },

      // Выход из системы
      logout: async () => {
        authEpoch += 1;
        set({ isLoading: true });

        try {
          await authApi.logout();
        } catch {
          // Игнорируем ошибки при logout
        } finally {
          clearTokens();
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          // Принудительный редирект на /login, чтобы пользователь не
          // оставался на authenticated-странице с очищенным state
          // и не получал 401 на каждый последующий запрос.
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      },

      /**
       * Проверка текущей аутентификации через /auth/me
       * @contract Сессию гасит ТОЛЬКО ответ 401; прочие ошибки сессию не трогают
       * @requires accessToken в client.ts (иначе — сразу «не аутентифицирован»)
       * @ensures Параллельные вызовы делят ОДИН запрос /auth/me
       * @ensures Уже аутентифицированный юзер перепроверяется фоном: isLoading не
       *   переключается — Layout не размонтирует страницу (S5-53: размонтирование →
       *   повторный маунт карточек → новый checkAuth давал бесконечную петлю /auth/me)
       * @ensures Не-401 (429/5xx/сеть) сам токены не очищает (S5-53: 429 от троттла BFF
       *   трактовался как «токен недействителен» и разлогинивал); если интерсептор уже
       *   очистил токены (refresh упал) — стор сбрасывается в «не аутентифицирован»
       * @ensures Ответ, пришедший после login/logout (сменилась эпоха сессии), стор и
       *   токены не трогает — isLoading в этом случае выставляет сменившее эпоху действие
       * @modifies user, isAuthenticated, isLoading; при 401 — токены (clearTokens)
       * @business_impact Ложный выход на /integrations у каждого юзера
       */
      checkAuth: () => {
        if (checkAuthInFlight) return checkAuthInFlight;

        const token = getAccessToken();

        if (!token) {
          set({ isLoading: false, isAuthenticated: false, user: null });
          return Promise.resolve();
        }

        if (!get().isAuthenticated) set({ isLoading: true });

        const epoch = authEpoch;

        checkAuthInFlight = (async () => {
          try {
            const user = await authApi.getCurrentUser();
            // Сессия сменилась (logout/новый login) — ответ относится к старой
            if (authEpoch !== epoch) return;
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch (error) {
            // Сессия сменилась — не стираем НОВЫЕ токены и чужое состояние
            if (authEpoch !== epoch) return;
            if (isAxiosError(error) && error.response?.status === 401) {
              // Токен недействителен (refresh, если был, интерсептор уже пробовал)
              clearTokens();
              set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
              });
            } else if (!getAccessToken()) {
              // Интерсептор уже погасил сессию (refresh упал не-401) — стор синхронизируем с токенами.
              // Обход до S5-55 (единый шов завершения сессии).
              set({ user: null, isAuthenticated: false, isLoading: false });
            } else {
              // Троттл/сбой сервера/сеть — о валидности токена ничего не говорят
              set({ isLoading: false });
            }
          } finally {
            checkAuthInFlight = null;
          }
        })();

        return checkAuthInFlight;
      },

      // Прямая установка пользователя (для demo/local входа)
      setUser: (user: UserInfo) => {
        authEpoch += 1;
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      // Очистить ошибку
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

// Вспомогательный хук для получения данных о пользователе
export function useUser(): UserInfo | null {
  return useAuthStore((state) => state.user);
}

// Вспомогательный хук для проверки аутентификации
export function useIsAuthenticated(): boolean {
  return useAuthStore((state) => state.isAuthenticated);
}
